"use client";

import * as React from "react";
import { cn } from "../../lib";
import { SQ_GREY_TEXT_CLASS } from "../../lib/sq-color-runtime-exports";
import { RotatingText } from "../react-bits/rotating-text";

export type RotatingPromptVisualVariant = "hub" | "marketing";

export interface RotatingPromptProps {
	/** When empty, only the rotating pill is shown (unless `staticTextsByRotatingIndex` is set). */
	staticText: string;
	rotatingTexts: readonly string[];
	/**
	 * Optional per-entry static prefix aligned with `rotatingTexts[i]` (same length when set).
	 * When provided, the purple prefix updates with each rotation (Create/Learn hub-style prompts).
	 */
	staticTextsByRotatingIndex?: readonly string[];
	reducedMotionUi: boolean;
	visualVariant: RotatingPromptVisualVariant;
	className?: string;
	/**
	 * Marketing only: set outer `minWidth` to the widest full quote line (static + each rotating clause)
	 * so layout width stays stable under annotations (e.g. rough-notation) while text rotates.
	 */
	lineWidthStabilize?: boolean;
	/** Below `sm`, static prefix on its own row above the rotating pill. */
	splitStaticRotatingBelowSm?: boolean;
}

const HUB_ROTATING_SURFACE = cn(
	"overflow-hidden rounded-sm bg-white px-2 py-px font-extrabold shadow-sm sm:px-2.5",
	SQ_GREY_TEXT_CLASS,
);

const MARKETING_ROTATING_SURFACE = cn(
	"overflow-hidden rounded-md bg-white px-3 py-1 font-extrabold shadow-md sm:px-4 sm:py-1.5",
	SQ_GREY_TEXT_CLASS,
);

const VARIANT_LAYOUT = {
	hub: {
		outer: "flex w-full justify-center px-2",
		emptyParagraph:
			"text-center text-sm leading-snug text-foreground sm:text-base",
		blockquote: "max-w-full flex-wrap justify-center gap-x-1.5 sm:gap-x-2",
		quoteMarks:
			"font-black leading-none text-foreground/30 text-2xl sm:text-3xl",
		innerRow:
			"inline-flex max-w-full flex-wrap items-baseline justify-center gap-x-2 text-center text-sm leading-snug sm:gap-x-2.5 sm:text-base",
		staticSpan: "font-medium text-foreground",
		rotatingSurface: HUB_ROTATING_SURFACE,
		rotatingMainExtra: "inline-flex max-w-[min(100%,28rem)] justify-center",
		splitLevel: "overflow-hidden pb-px sm:pb-0.5",
	},
	marketing: {
		outer: "flex w-full justify-center px-2",
		emptyParagraph:
			"max-w-4xl text-center text-lg leading-snug text-white sm:text-xl md:text-2xl",
		blockquote:
			"max-w-full flex-wrap justify-center gap-x-2 sm:gap-x-2.5 md:gap-x-3",
		quoteMarks:
			"font-black leading-none text-white/35 text-3xl sm:text-4xl md:text-5xl",
		innerRow:
			"inline-flex max-w-full flex-wrap items-baseline justify-center gap-x-2.5 text-center text-lg leading-snug text-white sm:gap-x-3 sm:text-xl md:gap-x-3.5 md:text-2xl md:leading-snug",
		staticSpan: "font-extrabold text-white",
		rotatingSurface: MARKETING_ROTATING_SURFACE,
		rotatingMainExtra: "inline-flex max-w-[min(100%,40rem)] justify-center",
		splitLevel: "overflow-hidden pb-0.5 sm:pb-1",
	},
} as const;

/**
 * Hub-style static line + white pill + rotating clause, with optional reduced-motion fallback.
 * Plain-quote layout matches `BlockQuote` `variant="plain"` in the web app (no app-package dependency).
 */
interface RotatingPromptMeasureLine {
	staticText: string;
	rotatingText: string;
}

function useMarketingLineWidthStabilizePx(
	enabled: boolean,
	measureLines: readonly RotatingPromptMeasureLine[],
	measureRootRef: React.RefObject<HTMLDivElement | null>,
): number | undefined {
	const [minPx, setMinPx] = React.useState<number | undefined>(undefined);
	const measureKey = measureLines
		.map((l) => `${l.staticText}\u0000${l.rotatingText}`)
		.join("\u0001");

	const recalc = React.useCallback(() => {
		void measureKey;
		if (!enabled) {
			setMinPx(undefined);
			return;
		}
		const root = measureRootRef.current;
		if (!root) {
			return;
		}
		const rows = root.querySelectorAll("[data-rp-line-measure]");
		let max = 0;
		for (const row of rows) {
			max = Math.max(max, row.getBoundingClientRect().width);
		}
		if (max > 0) {
			setMinPx(Math.ceil(max));
		}
	}, [enabled, measureRootRef, measureKey]);

	React.useLayoutEffect(() => {
		if (!enabled) {
			setMinPx(undefined);
			return;
		}
		recalc();
		let cancelled = false;
		const t = window.setTimeout(() => {
			if (!cancelled) {
				recalc();
			}
		}, 0);
		return () => {
			cancelled = true;
			window.clearTimeout(t);
		};
	}, [enabled, recalc]);

	React.useLayoutEffect(() => {
		if (!enabled) {
			return;
		}
		const onResize = () => {
			recalc();
		};
		window.addEventListener("resize", onResize, { passive: true });
		return () => {
			window.removeEventListener("resize", onResize);
		};
	}, [enabled, recalc]);

	React.useEffect(() => {
		if (!enabled || typeof document === "undefined") {
			return;
		}
		const fonts = document.fonts;
		if (!fonts?.ready) {
			return;
		}
		let alive = true;
		void fonts.ready.then(() => {
			if (alive) {
				recalc();
			}
		});
		return () => {
			alive = false;
		};
	}, [enabled, recalc]);

	return minPx;
}

export function RotatingPrompt({
	staticText,
	rotatingTexts,
	staticTextsByRotatingIndex,
	reducedMotionUi,
	visualVariant,
	className,
	lineWidthStabilize = false,
	splitStaticRotatingBelowSm = false,
}: RotatingPromptProps) {
	const layout = VARIANT_LAYOUT[visualVariant];
	const splitBelowSm = Boolean(splitStaticRotatingBelowSm);
	const rotating = [...rotatingTexts];
	const first = rotating[0] ?? "";
	const pairedStatics =
		staticTextsByRotatingIndex &&
		staticTextsByRotatingIndex.length === rotatingTexts.length &&
		rotatingTexts.length > 0
			? staticTextsByRotatingIndex
			: null;

	const measureLines =
		React.useMemo((): readonly RotatingPromptMeasureLine[] => {
			if (rotatingTexts.length === 0) {
				return [];
			}
			if (pairedStatics) {
				return rotatingTexts.map((phrase, i) => ({
					staticText: pairedStatics[i] ?? "",
					rotatingText: phrase,
				}));
			}
			return rotatingTexts.map((phrase) => ({
				staticText,
				rotatingText: phrase,
			}));
		}, [rotatingTexts, staticText, pairedStatics]);

	const measureFingerprint = measureLines
		.map((l) => `${l.staticText}\u0000${l.rotatingText}`)
		.join("\u0001");

	const [activeRotatingIndex, setActiveRotatingIndex] = React.useState(0);
	const onPairedRotatingIndex = React.useCallback((index: number) => {
		setActiveRotatingIndex(index);
	}, []);
	React.useEffect(() => {
		void measureFingerprint;
		setActiveRotatingIndex(0);
	}, [measureFingerprint]);

	const displayStatic = pairedStatics
		? (pairedStatics[activeRotatingIndex] ?? "")
		: staticText;
	const hasStaticPrefix = displayStatic.trim().length > 0;

	const stabilize =
		Boolean(lineWidthStabilize) &&
		!splitBelowSm &&
		visualVariant === "marketing" &&
		measureLines.length > 0;

	const measureRootRef = React.useRef<HTMLDivElement>(null);
	const minOuterPx = useMarketingLineWidthStabilizePx(
		stabilize,
		measureLines,
		measureRootRef,
	);

	return (
		<>
			{stabilize ? (
				<div
					ref={measureRootRef}
					className="pointer-events-none fixed top-0 left-0 -z-50 opacity-0"
					aria-hidden
				>
					{measureLines.map((line, lineIndex) => (
						<blockquote
							key={`${lineIndex}\u0000${line.staticText}\u0000${line.rotatingText}`}
							data-rp-line-measure=""
							className={cn(
								"m-0 inline-flex w-max max-w-full flex-wrap items-baseline justify-center gap-x-0 border-0 bg-transparent p-0 shadow-none backdrop-blur-none",
								layout.blockquote,
							)}
						>
							<span
								aria-hidden
								className={cn(
									"pointer-events-none shrink-0 select-none font-black leading-none",
									layout.quoteMarks,
								)}
							>
								&ldquo;
							</span>
							<span className="inline min-w-0">
								<span className={layout.innerRow}>
									{line.staticText.trim().length > 0 ? (
										<span className={layout.staticSpan}>
											{line.staticText}
										</span>
									) : null}
									<span
										className={cn(
											layout.rotatingMainExtra,
											layout.rotatingSurface,
										)}
									>
										{line.rotatingText}
									</span>
								</span>
							</span>
							<span
								aria-hidden
								className={cn(
									"pointer-events-none shrink-0 select-none font-black leading-none",
									layout.quoteMarks,
								)}
							>
								&rdquo;
							</span>
						</blockquote>
					))}
				</div>
			) : null}
			<div
				className={cn(
					layout.outer,
					minOuterPx != null && "w-auto",
					className,
				)}
				aria-live="polite"
				style={
					minOuterPx != null
						? { minWidth: `${minOuterPx}px`, maxWidth: "100%" }
						: undefined
				}
			>
				{rotating.length === 0 ? (
					<p className={layout.emptyParagraph}>
						<span className="font-medium">{displayStatic}</span>
					</p>
				) : (
					<blockquote
						className={cn(
							"m-0 inline-flex items-baseline gap-x-0 border-0 bg-transparent p-0 shadow-none backdrop-blur-none",
							layout.blockquote,
							splitBelowSm &&
								"max-sm:inline-block max-sm:w-fit max-sm:max-w-full sm:inline-flex sm:items-baseline",
						)}
					>
						<span
							aria-hidden
							className={cn(
								"pointer-events-none shrink-0 select-none font-black leading-none",
								layout.quoteMarks,
								splitBelowSm && "max-sm:hidden",
							)}
						>
							&ldquo;
						</span>
						<span
							className={cn(
								"inline min-w-0",
								splitBelowSm && "max-sm:block max-sm:w-full",
							)}
						>
							<span
								className={cn(
									layout.innerRow,
									splitBelowSm &&
										"max-sm:flex max-sm:w-full max-sm:flex-col max-sm:items-center max-sm:justify-center max-sm:gap-x-0 max-sm:gap-y-2",
								)}
							>
								{hasStaticPrefix ? (
									<span
										className={cn(
											splitBelowSm &&
												"relative max-sm:inline-block max-sm:w-fit max-sm:max-w-full",
										)}
									>
										{splitBelowSm ? (
											<span
												aria-hidden
												className="pointer-events-none absolute inset-0 overflow-visible select-none sm:hidden"
											>
												<span
													className={cn(
														layout.quoteMarks,
														"absolute top-0 left-0 -translate-x-6 -translate-y-1 leading-none",
													)}
												>
													&ldquo;
												</span>
											</span>
										) : null}
										<span
											className={cn(
												layout.staticSpan,
												splitBelowSm &&
													"relative z-[1] max-sm:block max-sm:text-center",
											)}
										>
											{displayStatic}
										</span>
									</span>
								) : null}
								<span
									className={cn(
										splitBelowSm &&
											"relative max-sm:inline-flex max-sm:w-fit max-sm:max-w-full max-sm:justify-center",
									)}
								>
									{splitBelowSm ? (
										<span
											aria-hidden
											className="pointer-events-none absolute inset-0 overflow-visible select-none sm:hidden"
										>
											<span
												className={cn(
													layout.quoteMarks,
													"absolute right-0 bottom-0 translate-x-6 translate-y-6 leading-none",
												)}
											>
												&rdquo;
											</span>
										</span>
									) : null}
									<span
										className={cn(
											splitBelowSm &&
												"relative z-[1] inline-flex justify-center",
										)}
									>
										{reducedMotionUi ? (
											<span
												className={cn(
													layout.rotatingSurface,
												)}
											>
												{first}
											</span>
										) : (
											<RotatingText
												texts={rotating}
												onNext={
													pairedStatics
														? onPairedRotatingIndex
														: undefined
												}
												mainClassName={cn(
													layout.rotatingMainExtra,
													layout.rotatingSurface,
												)}
												staggerFrom="last"
												initial={{ y: "100%" }}
												animate={{ y: 0 }}
												exit={{ y: "-120%" }}
												staggerDuration={0.025}
												splitLevelClassName={
													layout.splitLevel
												}
												elementLevelClassName={
													SQ_GREY_TEXT_CLASS
												}
												transition={{
													type: "spring",
													damping: 30,
													stiffness: 400,
												}}
												rotationInterval={2640}
												splitBy="characters"
												auto
												loop
											/>
										)}
									</span>
								</span>
							</span>
						</span>
						<span
							aria-hidden
							className={cn(
								"pointer-events-none shrink-0 select-none font-black leading-none",
								layout.quoteMarks,
								splitBelowSm && "max-sm:hidden",
							)}
						>
							&rdquo;
						</span>
					</blockquote>
				)}
			</div>
		</>
	);
}
